import React, { useContext, useRef } from 'react';
import { MobXProviderContext, observer } from 'mobx-react';
import classNames from 'classnames';

import { LessonSlideIds } from '../../../Constants';

import navMenuService from '../../services/NavMenuService';

import useAccessibilityClick from '../../../hooks/useAccessibilityClick';

import VerticalGotoItem from './VerticalGotoItem';
import VerticalGotoSection from './VerticalGotoSection';

import utils from '../../services/UtilsService';
import { register } from '../../../i18n';

import '../../../css/VerticalGotoMenu.scss';

const t = register('NavigationBar');
const t2 = register('AriaLabels');

/**
 * Vertical "GO TO" table-of-contents menu. Anchored to the top-left of the
 * content area as a flyout. The vertical "Go To" tab toggles the panel open;
 * the panel lists the lesson title page (if present), each lesson section with
 * its items, and the summary page (if present).
 *
 * Shown only when lessonOptions.hasVerticalGotoMenu is true (mutually exclusive
 * with the legacy footer GO TO designs).
 */
const VerticalGotoMenu = observer(({ panelOnly = false } = {}) => {
  const {
    lessonManager,
    navMenuManager,
    responseManager,
    userManager
  } = useContext(MobXProviderContext);

  const tabRef = useRef();
  const isOpen = navMenuManager.gotoMenuOpen;

  useAccessibilityClick(tabRef, (event) => {
    navMenuService.gotoButtonHandler(event);
  });

  const visibleSlides = lessonManager.getFilteredSlides();
  const hasTitlePage = visibleSlides.indexOf(LessonSlideIds.TITLE_PAGE) > -1;
  const hasSummaryPage = visibleSlides.indexOf(LessonSlideIds.SUMMARY_PAGE) > -1;

  const getLockInfo = (lessonElementId) => {
    const model = lessonManager.getSlideModel(lessonElementId);
    const lessonElement = lessonManager.getLessonElement(lessonElementId);
    const lessonElementState = responseManager.getLessonElementState(lessonElementId);
    let isLocked = false;
    let childState = null;
    if (model && model.isActivity && lessonElement?.lessonElementIds?.length > 0) {
      childState = responseManager.getLessonElementState(lessonElement.lessonElementIds[0]);
      isLocked = !!(childState && childState.isDependencyLocked);
    } else {
      isLocked = !!(lessonElementState && lessonElementState.isDependencyLocked);
    }
    return { isLocked, childState };
  };

  // Use the same numbering rules as the rest of the player: test items use their
  // canonical questionNumber, activities use their questionRange, and everything
  // else (e.g. student/teacher instructions, resources) has no number.
  const getItemNumber = (lessonElementId) => {
    const model = lessonManager.getSlideModel(lessonElementId);
    if (!model) {
      return null;
    }
    const value = model.isActivity ? model.questionRange : model.questionNumber;
    if (value === null || value === undefined || value === '') {
      return null;
    }
    return value;
  };

  const buildGotoItem = (lessonElementId) => {
    const { isLocked, childState } = getLockInfo(lessonElementId);
    return {
      lessonElementId,
      itemNumber: getItemNumber(lessonElementId),
      isLocked,
      childState,
    };
  };

  const sections = (lessonManager.sectionLessonElementIds || []).map((sectionLessonElementId) => {
    // Like the old GO TO: list the activity parent (with its question range), not the children.
    const childIds = lessonManager.getSectionLessonElementIdList(sectionLessonElementId, userManager.isStudent, false);
    const items = childIds.map((lessonElementId) => buildGotoItem(lessonElementId));
    return { sectionLessonElementId, items };
  });

  const sectionItemIds = new Set();
  sections.forEach(({ items }) => {
    items.forEach(({ lessonElementId }) => sectionItemIds.add(lessonElementId));
  });

  // Slides not in any section (e.g. item preview / contentViewer) still appear at the root.
  const orphanGotoItems = [];
  visibleSlides.forEach((lessonElementId) => {
    if (lessonElementId === LessonSlideIds.TITLE_PAGE || lessonElementId === LessonSlideIds.SUMMARY_PAGE) {
      return;
    }
    if (sectionItemIds.has(lessonElementId)) {
      return;
    }
    const model = lessonManager.getSlideModel(lessonElementId);
    if (!model || model.isActivityPart || model.isTitlePage || model.isSummaryPage) {
      return;
    }
    if (model.isTestItem || model.isInstruction || model.isResource || model.isActivity) {
      orphanGotoItems.push(buildGotoItem(lessonElementId));
    }
  });

  const sectionLessonElementIds = lessonManager.sectionLessonElementIds || [];
  const flattenSectionsToRoot = sectionLessonElementIds.length === 1 &&
    utils.isNullOrEmpty(lessonManager.getSectionModel(sectionLessonElementIds[0])?.title);
  const rootSectionItems = flattenSectionsToRoot ? (sections[0]?.items || []) : [];

  const renderGotoItems = (items) => items.map(({ lessonElementId, itemNumber, isLocked, childState }) => (
    <VerticalGotoItem
      key={`${lessonElementId}-vertical-goto-item`}
      childState={childState}
      isLocked={isLocked}
      itemNumber={itemNumber}
      lessonElementId={lessonElementId} />
  ));

  const panel = (
    <div className='vertical-goto-scroll'>
      {hasTitlePage && (
        <ol className='vertical-goto-item-list title-page-list'>
          <VerticalGotoItem
            itemNumber={null}
            lessonElementId={LessonSlideIds.TITLE_PAGE} />
        </ol>
      )}
      {flattenSectionsToRoot ? (
        rootSectionItems.length > 0 && (
          <ol className='vertical-goto-item-list'>
            {renderGotoItems(rootSectionItems)}
          </ol>
        )
      ) : (
        <>
          {sections.map(({ sectionLessonElementId, items }) => (
            <VerticalGotoSection
              key={`${sectionLessonElementId}-vertical-goto-section`}
              items={items}
              sectionLessonElementId={sectionLessonElementId} />
          ))}
          {orphanGotoItems.length > 0 && (
            <ol className='vertical-goto-item-list'>
              {renderGotoItems(orphanGotoItems)}
            </ol>
          )}
        </>
      )}
      {hasSummaryPage && (
        <ol className='vertical-goto-item-list summary-page-list'>
          <VerticalGotoItem
            itemNumber={null}
            lessonElementId={LessonSlideIds.SUMMARY_PAGE} />
        </ol>
      )}
    </div>
  );

  if (panelOnly) {
    return panel;
  }

  return (
    <div className={classNames('vertical-goto-menu', { open: isOpen, closed: !isOpen })}>
      <div className='vertical-goto-panel' id='vertical-goto-panel'>
        {panel}
      </div>
      <div
        ref={tabRef}
        aria-controls='vertical-goto-panel'
        aria-expanded={isOpen}
        aria-label={isOpen ? t2('closethevavigationmenu') : t2('showthevavigationmenu')}
        className='vertical-goto-tab'
        role='button'
        tabIndex='0'>
        <span className='vertical-goto-tab-label'>{t('goto')}</span>
      </div>
    </div>
  );
});

export default VerticalGotoMenu;
